<template>
    <div class="period-buttons flex items-center flex-wrap text-sm leading-loose xs:pl-0 xs:justify-center mt-1">
        <span class="font-bold xs:hidden mr-2 mt-1">Period:</span>
        <div
            @click="selectButton(button)"
            v-for="button in buttons"
            class="mt-1_"
        >
            <span
                class="period-button btn btn-chart-period btn-xs btn-shadow-none mt-1"
                :class="{ active: JSON.stringify(button) === JSON.stringify(selectedButton) }"
            >
                {{ button.label }}
            </span>
        </div>

        <div class="inline-block mb-1">
            <label for="minutes" class="leading-loose align-middle inline-flex text-xs mt-1">
                <input id="minutes"
                    @blur="changeMinutes"
                    @keyup.enter="changeMinutes"
                    placeholder="mins."
                    type="number"
                    v-model.number="minutes"
                    class="form-input-inline-w-none form-input-sm-inline w-12 sm:w-16 text-xs"
                    aria-label="Minutes"
                />
            </label>
        </div>

        <div v-if="displaySelect()" class="inline-block ml-2 xs:w-1/3 xs:mb-1 xs:mr-2 sm:w-auto">
            <select @change="selectOption(selectedOption)" v-model="selectedOption" class="form-control form-control-sm form-control-no-shadow" aria-label="Period">
                <option :value="option" v-for="option in selectOptions">{{ option.label }}</option>
            </select>
        </div>

        <span class="ml-2" v-tooltip="{
            content: `Checking Default sets the style of chart for all links on the site. Use this when you want to view several Intraday, Weekly, or Monthly charts in row. Un-check the default box to return to Daily Charts as the default time period.`,
            placement: 'auto-start',
            theme: 'info',
        }">
            <label for="default-period">
                <input v-model="isDefault" @change="toggleDefault" id="default-period" type="checkbox" />
                <i class="ml-1 align-middle">Default</i>
            </label>
        </span>
    </div>
    <flash-message :secondsToDisplay="3" ref="flash"></flash-message>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';

const props = defineProps({
    initialDefault: Object,
    initialAggregation: Object,
    section: String,
});

const emit = defineEmits(['changed']);
const flash = ref(null);

const selectedButton = ref(null);
const selectedOption = ref(null);
const minutes = ref(null);
const defaultValue = ref(null);
const isDefault = ref(false);

const periodMap = {
    Intraday: 'intraday',
    Day: 'daily',
    Week: 'weekly',
    Month: 'monthly'
};

const specMap = {
    Nearest: 'nearby',
    None: 'contract'
};

const buttons = [
    { aggregation: { unit: 'Day', size: 1, spec: 'None', isContractVolume: true }, label: 'D' },
    { aggregation: { unit: 'Week', size: 1, spec: 'Nearest', isContractVolume: false }, label: 'W' },
    { aggregation: { unit: 'Month', size: 1, spec: 'Nearest', isContractVolume: false }, label: 'M' },
    { aggregation: { unit: 'Intraday', size: 30, spec: 'None', isContractVolume: true }, label: '30m' },
    { aggregation: { unit: 'Intraday', size: 60, spec: 'None', isContractVolume: true }, label: '60m' },
    { aggregation: { unit: 'Intraday', size: 240, spec: 'None', isContractVolume: true }, label: '240m' },
];

const selectOptions = [
    { aggregation: { unit: 'Intraday', size: 30, spec: 'None', isContractVolume: true }, label: 'Intraday' },
    { aggregation: { unit: 'Day', size: 1, spec: 'None', isContractVolume: true }, label: 'Daily' },
    { aggregation: { unit: 'Day', size: 1, spec: 'Nearest', isContractVolume: false }, label: 'Daily Nearby' },
    { aggregation: { unit: 'Week', size: 1, spec: 'None', isContractVolume: true }, label: 'Weekly' },
    { aggregation: { unit: 'Week', size: 1, spec: 'Nearest', isContractVolume: false }, label: 'Weekly Nearby' },
    { aggregation: { unit: 'Month', size: 1, spec: 'None', isContractVolume: true }, label: 'Monthly' },
    { aggregation: { unit: 'Month', size: 1, spec: 'Nearest', isContractVolume: false }, label: 'Monthly Nearby' },
];

function findSelectedButton() {
    if (!props.initialAggregation) return;

    if (props.initialAggregation.unit === 'Intraday') {
        // We have "custom" intraday minutes
        // Don't select any buttons
        if (![30, 60, 240].includes(props.initialAggregation.size)) return;

        return buttons.find(button => button.aggregation.size === props.initialAggregation.size);
    }

    return buttons.find(button => button.aggregation.unit === props.initialAggregation.unit);
}

function getInitialDefault() {
    return checkIfIsDefault(props.initialDefault);
}

function toggleDefault() {
    if (!isDefault.value) {
        return updateDefaultPeriod(null);
    }

    const period = periodMap[selectedOption.value.aggregation.unit];
    const spec = specMap[selectedOption.value.aggregation.spec];

    const params = { period, spec };

    if (period === 'intraday') {
        const mMinutes = minutes.value || selectedButton.value.aggregation.size;
        if (mMinutes) {
            params.minutes = mMinutes;
        }
    }

    updateDefaultPeriod(params);
}

function checkIfIsDefault(pObj) {
    const selectedOption = findSelectedOption();
    const selectedButton = findSelectedButton();

    const keyByValue = (fObject, val) => Object.keys(fObject).find(key => fObject[key] === val);

    const period = keyByValue(periodMap, pObj.period);
    const spec = keyByValue(specMap, pObj.spec);

    let mMinutes = true;

    if (period === 'Intraday') {
        const mins = parseInt(pObj.minutes);

        if (findMinutes()) {
            mMinutes = findMinutes() === mins;
        } else if (selectedButton) {
            mMinutes = selectedButton.aggregation.size === mins;
        } else {
            mMinutes = selectedOption.aggregation.size === mins;
        }
    }

    return selectedOption.aggregation.spec === spec && selectedOption.aggregation.unit === period && mMinutes;
}

function findSelectedOption() {
    if (!props.initialAggregation) return;

    return selectOptions.find(mOption => {
        return mOption.aggregation.spec === props.initialAggregation.spec
            && mOption.aggregation.unit === props.initialAggregation.unit;
    });
}

function selectButton(pAggregation) {
    selectedButton.value = pAggregation;
    selectedOption.value = findSelectedOption();
    minutes.value = null;

    emit('changed', getAction(pAggregation));
}

function selectOption(pAggregation) {
    selectedButton.value = findSelectedButton();
    minutes.value = null;

    emit('changed', getAction(pAggregation));
}

function getAction(pAggregation) {
    return {
        id: 'Aggregation',
        context: pAggregation.aggregation
    }
}

function findMinutes() {
    if (props.initialAggregation && props.initialAggregation.unit === 'Intraday') {
        if (![30, 60, 240].includes(props.initialAggregation.size)) {
            return props.initialAggregation.size;
        }
    }
}

function displaySelect() {
    return ['futures', 'futures-options', 'spreads'].includes(props.section);
}

function validateMinutes() {
    if (!minutes.value || minutes.value.length === 0 || isNaN(parseInt(minutes.value))) {
        minutes.value = null;
        return false;
    }

    return true;
}

function changeMinutes() {
    if (!validateMinutes()) {
        return;
    }

    const action = {
        id: 'Aggregation',
        context: {
            size: minutes.value,
            unit: 'Intraday',
            spec: 'None'
        }
    };

    selectedButton.value = null;

    emit('changed', action);
}

function updateDefaultPeriod(default_period) {
    const route = window.route('api.preferences');

    const params = {
        default_period
    };

    axios.post(route, params)
        .then((response) => {
            flash.value.display('Updated default period');
        }).catch((error) => {
            flash.value.error('Error updating default period');
        });
}

watch(() => props.initialAggregation, (newVal) => {
    if (newVal) {
        selectedButton.value = findSelectedButton();
        selectedOption.value = findSelectedOption();
        minutes.value = findMinutes();
        isDefault.value = checkIfIsDefault(defaultValue.value);
    }
});

onMounted(() => {
    if (props.section !== 'futures') {
        buttons.map((btn) => {
            btn.aggregation.spec = 'None';
            return btn;
        })
    }

    selectedButton.value = findSelectedButton();
    selectedOption.value = findSelectedOption();
    minutes.value = findMinutes();
    defaultValue.value = props.initialDefault;
    isDefault.value = getInitialDefault();
});
</script>

<style lang="scss">
.period-buttons {
    // display: inline-block;

    .active, .active:hover {
        @apply bg-blue-600 text-white border border-blue-600;
    }

    .period-button {
        @apply cursor-pointer px-2 ml-px;
    }

    .period-button-spacer {
        @apply w-1;
    }
}
</style>
